import { ConnectedRouter } from 'connected-react-router';
import React from 'react';
import { ReactReduxContext } from 'react-redux';
import { SWRConfig } from 'swr';
import { CDNContext } from '@peloton/cloudinary';
import { InjectIntlContext } from '@peloton/internationalize/context';
import { history } from '@peloton/redux';
import { BootupEvents } from '@members/analytics/bootupEvents';
import { ClientProvider } from '@members/api';
import OauthProvider from '@members/auth/OauthProvider';
import UserFetcher from '@members/auth/UserFetcher';
import { useCopyClient } from '@members/copy';
import CopyProvider from '@members/copy/CopyProvider/CopyProvider';
import Overlay from '@members/feature-toggles/Overlay';
import { GraphqlProvider } from '@members/graphql/GraphqlProvider';
import { Internationalize } from '@members/localization/Internationalize';
import { LocaleSetter } from '@members/localization/LocaleSetter';
import { OneTrustPopup } from '@members/onetrust/OneTrustPopup';
import { getAjsUserId } from '@members/optimizely/utils';
import { PersistGate } from '@members/persist';
import { TierProvider } from '@members/tiering/TierProvider';
import { ToggleGate } from '@members/toggle-gate/ToggleGate';
import { env } from '../_lib/env';

// These providers need to be defined outside of the tree where Redux is setup for hooks to work as expected
function AsyncCopyProviders({ children }) {
  const copyClient = useCopyClient();
  return (
    <CopyProvider copyClient={copyClient}>
      <CDNContext>{children}</CDNContext>
    </CopyProvider>
  );
}

/**
 * Consolidates all global React context providers to a single component
 */
export default function GlobalContextProviders({ children, reduxStore, reduxClient }) {
  return (
    <OauthProvider apiEnv={env.extLink.api} reduxClient={reduxClient}>
      <SWRConfig
        value={{
          revalidateOnFocus: false,
        }}
      >
        <ClientProvider client={reduxClient}>
          <Internationalize>
            <InjectIntlContext>
              <ToggleGate userId={getAjsUserId()}>
                <ConnectedRouter history={history} context={ReactReduxContext}>
                  <UserFetcher>
                    <TierProvider>
                      <LocaleSetter>
                        <PersistGate store={reduxStore}>
                          <GraphqlProvider>
                            <AsyncCopyProviders children={children} />
                            <BootupEvents />
                            <OneTrustPopup />
                            <Overlay />
                          </GraphqlProvider>
                        </PersistGate>
                      </LocaleSetter>
                    </TierProvider>
                  </UserFetcher>
                </ConnectedRouter>
              </ToggleGate>
            </InjectIntlContext>
          </Internationalize>
        </ClientProvider>
      </SWRConfig>
    </OauthProvider>
  );
}
